import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CATEGORIES } from "@/lib/categories";
import { toast } from "sonner";
import { Loader2, Send, Calendar, Sparkles } from "lucide-react";
import { formatApiError } from "@/lib/api";

function formatResetDate(iso) {
  if (!iso) return "the start of next month";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "the start of next month";
  }
}

export default function PostTip() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [text, setText] = useState("");
  const [category, setCategory] = useState("groceries");
  const [posting, setPosting] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post("/tips", { text, category });
      await refresh();
      toast.success("Thanks for sharing! Your community appreciates it. 💫");
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "post_limit_reached") {
        setLimitInfo(detail);
      } else {
        toast.error(formatApiError(detail, "Could not post tip"));
      }
    }
    setPosting(false);
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <p className="text-sm font-semibold text-[#2A9D8F] tracking-widest uppercase">Share</p>
        <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-tight">
          Share a <span className="text-[#E8621A]">cost-saving tip.</span>
        </h1>
        <p className="mt-3 text-[#1B2A4A]/70">
          The best tips are specific — a store name, a dollar amount, a trick that worked for you.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6 bg-white rounded-3xl border border-[#1B2A4A]/8 p-5 sm:p-8" data-testid="post-tip-form">
        <div>
          <label className="block text-sm font-semibold text-[#1B2A4A] mb-3">Pick a category</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`post-category-${c.id}`}
                  onClick={() => setCategory(c.id)}
                  className={`flex flex-col items-center gap-1.5 rounded-2xl border p-3 transition-colors duration-200 ${
                    active ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-[#1B2A4A] border-[#1B2A4A]/10 hover:border-[#E8621A]/40"
                  }`}
                  style={active ? {} : { color: c.color, borderColor: `${c.color}30` }}
                >
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                  <span className="text-[11px] font-semibold text-center leading-tight">{c.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#1B2A4A] mb-2">Your tip</label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="post-text"
            placeholder="e.g., I switched to Aldi for our weekly staples and save about $150 a month..."
            rows={5}
            className="rounded-2xl border-[#1B2A4A]/15 bg-white focus-visible:ring-2 focus-visible:ring-[#E8621A]/50 resize-none"
            maxLength={500}
          />
          <p className="text-right text-xs text-[#1B2A4A]/50 mt-1">{text.length}/500</p>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)} className="rounded-full" data-testid="post-cancel">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={posting || !text.trim()}
            data-testid="post-submit"
            className="rounded-full bg-[#E8621A] hover:bg-[#D05615] px-6 transition-colors duration-200"
          >
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-2" /> Share tip</>)}
          </Button>
        </div>
      </form>

      <AlertDialog open={!!limitInfo} onOpenChange={(v) => !v && setLimitInfo(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="post-limit-dialog">
          <AlertDialogHeader>
            <div className="w-11 h-11 rounded-2xl bg-[#E8621A]/12 flex items-center justify-center mb-2">
              <Calendar className="w-5 h-5 text-[#E8621A]" />
            </div>
            <AlertDialogTitle className="font-heading text-2xl text-[#1B2A4A]">
              You've hit your Free tier limit for this month
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#1B2A4A]/75 leading-relaxed" data-testid="post-limit-message">
              You've shared {limitInfo?.current_count ?? 3} of {limitInfo?.limit ?? 3} tips this month.
              Your limit resets on{" "}
              <span className="font-semibold text-[#1B2A4A]" data-testid="post-limit-reset-date">
                {formatResetDate(limitInfo?.reset_at)}
              </span>
              . Upgrade to Community Member to share without limits.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-end gap-2">
            <AlertDialogCancel className="rounded-full" data-testid="post-limit-close">Not now</AlertDialogCancel>
            <Link to="/membership" data-testid="post-limit-upgrade">
              <AlertDialogAction className="rounded-full bg-[#E8621A] hover:bg-[#D05615] text-white">
                <Sparkles className="w-4 h-4 mr-2" /> See membership
              </AlertDialogAction>
            </Link>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
